/* eslint-disable prettier/prettier */
import {
  ActionIcon,
  Avatar,
  Badge,
  MantineThemeColorsOverride,
  MantineThemeOverride,
  Pagination,
  Select,
  Textarea,
  createTheme,
  TextInput,
  Autocomplete,
  Text,
  DEFAULT_THEME,
  mergeMantineTheme,
  Loader,
  MultiSelect,
  Chip,
  TagsInput,
  Checkbox,
  Button,
  SegmentedControl,
} from "@mantine/core"
import { DateInput } from "@mantine/dates"
import "@mantine/dates/styles.css"
import "@mantine/dropzone/styles.css"

import classes from "./css/Global.module.css"
import { variantColorResolver } from "./variantResolver.ts"

/**
 * Never export it!
 * Prefer using `const { colors } = useMantineTheme()`
 */
const colors: Partial<MantineThemeColorsOverride["colors"]> = {
  green: [
    "#E4F2E8",
    "#CDDFD6",
    "#BAD0C5",
    "#A8C1B5",
    "#96B2A4",
    "#739987",
    "#6F9382",
    "#5D8471",
    "#4B7661",
    "#306149",
  ],
  beige: [
    "#F6F2EE",
    "#F4EFEA",
    "#F2EDE7",
    "#F1EBE5",
    "#EFE8E2",
    "#EDE5DE",
    "#D7C5B6",
    "#C9B09B",
    "#B8987D",
    "#A47B58",
  ],
  blue: [
    "#E8EEFD",
    "#D5DEF8",
    "#BFCCF1",
    "#ABBCEB",
    "#98ACE5",
    "#7E9BEF",
    "#718CD9",
    "#5B7BD3",
    "#4B6DCE",
    "#2F56C5",
  ],
  red: [
    "#FEE4E7",
    "#F3C6CC",
    "#E8ABB3",
    "#DD8D97",
    "#CF6673",
    "#C24958",
    "#B73747",
    "#AF2B3C",
    "#A81F30",
    "#A01224",
  ],
  gray: [
    "#f9fafb",
    "#f3f4f6",
    "#e5e7eb",
    "#d1d5db",
    "#9ca3af",
    "#6b7280",
    "#4b5563",
    "#374151",
    "#21211F",
    "#111827",
  ],
  yellow: [
    "#FFFAEB",
    "#FCE588",
    "#FADB5F",
    "#F7C948",
    "#F0B429",
    "#DE911D",
    "#CB6E17",
    "#B44D12",
    "#8D2B0B",
    "#63171B",
  ],
  purple: [
    "#F5F3FF",
    "#EDE9FE",
    "#DDD6FE",
    "#C4B5FD",
    "#A78BFA",
    "#8B5CF6",
    "#7C3AED",
    "#6D28D9",
    "#5B21B6",
    "#4C1D95",
  ],
  primary: [
    "#F8FAF9",
    "#F3F5F4",
    "#F1F5F3",
    "#B9C1BE",
    "#ABC2B7",
    "#74837C",
    "#739987",
    "#173025",
    "#173025",
    "#173025",
  ],
  tagColorGreen: [
    "#f1ffe6",
    "#e4fbd2",
    "#c9f6a5",
    "#acf174",
    "#94ec4b",
    "#84e932",
    "#7be823",
    "#68ce15",
    "#5bb70b",
    "#4a9e00",
  ],
  tagColorBlue: [
    "#e6fcfc",
    "#daf2f2",
    "#b8e2e2",
    "#93d2d2",
    "#75c4c4",
    "#61bbbb",
    "#54b7b7",
    "#42a1a1",
    "#339090",
    "#197d7d",
  ],
  tagColorMauve: [
    "#edf2ff",
    "#dbe1f5",
    "#b6c1e4",
    "#8f9fd4",
    "#6d82c7",
    "#586fbf",
    "#4c66bc",
    "#3d56a6",
    "#344c96",
    "#284185",
  ],
  tagColorPink: [
    "#ffe9ef",
    "#ffd0db",
    "#fd9db4",
    "#fc678a",
    "#fb3c67",
    "#fc2451",
    "#fc1845",
    "#e10b37",
    "#c90030",
    "#b00028",
  ],
  tagColorYellow: [
    "#fffae3",
    "#fef2cf",
    "#fbe5a0",
    "#f8d66d",
    "#f6ca42",
    "#f4c228",
    "#f4be18",
    "#d9a70a",
    "#c19400",
    "#a77f00",
  ],
}

export type Color = keyof typeof colors

const fontFamily =
  "Poppins, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"

export const themeOverride: MantineThemeOverride = createTheme({
  // Colors
  variantColorResolver,
  colors: {
    ...colors,
    primary: colors.primary,
    secondary: colors.beige,
  },
  primaryShade: 6,
  primaryColor: "primary",
  black: "#000",
  // Fonts
  fontFamily,
  autoContrast: true,
  headings: { fontFamily },
  fontSizes: {
    xs: "0.75rem",
    sm: "0.85rem",
  },
  components: {
    Autocomplete: Autocomplete.extend({
      defaultProps: {
        variant: "filled",
        classNames: {
          input: classes.input,
          label: classes.label,
        },
        radius: "lg",
      },
    }),
    Avatar: Avatar.extend({
      defaultProps: {
        variant: "filled",
        classNames: classes,
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        size: "xs",
      },
      classNames: {
        input: classes.input,
        label: classes.label,
      },
    }),
    DateInput: DateInput.extend({
      defaultProps: {
        classNames: {
          input: classes.input,
          label: classes.label,
        },
        size: "xs",
      },
    }),

    ActionIcon: ActionIcon.extend({
      defaultProps: {
        color: "gray",
        variant: "transparent",
      },
    }),
    Button: Button.extend({
      defaultProps: {
        variant: "primary",
        loaderProps: {
          type: "dots",
        },
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        classNames: {
          input: classes.input,
          label: classes.label,
        },
        size: "xs",
      },
    }),
    Select: Select.extend({
      defaultProps: {
        size: "xs",
        classNames: {
          input: classes.input,
          label: classes.label,
        },
      },
    }),
    Pagination: Pagination.extend({
      defaultProps: {
        radius: "xl",
      },
    }),
    Text: Text.extend({
      defaultProps: {
        className: classes.text,
        fz: "xs",
        size: "xs",
      },
    }),
    Badge: Badge.extend({
      defaultProps: {
        variant: "light",
        autoContrast: true,
      },
    }),

    Loader: Loader.extend({
      defaultProps: {
        type: "bars",
      },
    }),
    Chip: Chip.extend({
      defaultProps: {
        classNames: {
          label: classes.chip,
        },
      },
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        size: "xs",
        classNames: classes,
      },
    }),
    TagsInput: TagsInput.extend({
      defaultProps: {
        size: "xs",
        className: classes.tagInput,
        classNames: {
          pill: classes.pill,
          input: classes.input,
          label: classes.label,
        },
      },
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        size: "xs",
        classNames: {
          input: classes.input,
          label: classes.label,
        },
      },
    }),
    SegmentedControl: SegmentedControl.extend({
      defaultProps: {
        size: "sm",
        color: "primary",
        bg: "white",
        autoContrast: true,
        withItemsBorders: false,
      },
    }),
  },
})

export const theme = mergeMantineTheme(DEFAULT_THEME, themeOverride)
